/**
 * CH585M 设备上报回填规则集 (Phase 4)
 *
 * 监听 `report:Key_user_data` 事件,把硬件主动上报的高频字段回填到缓存,
 * 让后续 `getXxx()` 调用直接命中缓存,不再触发硬件读。
 *
 * --- 关键约束 (design Components §4, requirements 4.1~4.9) ---
 *
 * 1. **字节布局必须与 sendAndWait 真实返回一致** (Requirement 4.7):
 *    回填的 `BackfillItem.data` 布局为 `[ACK_BYTE, CMD_BYTE, LENGTH, 0x00, ...actualData]`,
 *    其中 ACK_BYTE = 0x01 (read_ack_value),这与 `sendAndWait` feature report 响应格式一致。
 *    parser 统一从 offset 4 开始读取有效数据。
 *
 * 2. **长度必须与读取侧完全一致**:
 *    `makeKey(address, length)` 把 length 编进了缓存键,回填长度与 `sendRead` 的
 *    读取长度不一致时缓存**永远命不中**。同时 `data` 至少要有 `4 + length` 字节,
 *    否则命中后 parser 读 `getUint8(4 + i)` 会抛 RangeError。
 *
 * 3. **extract 是纯函数** (Requirement 4.8):
 *    不调用 sendRead / sendWrite / channel.send 等 SDK 方法。
 *
 * --- 为什么不回填灯光参数 ---
 *
 * 灯效的亮度/速度是通过 `getConfig()` 以**整块 8 字节**读出来的
 * (`descriptor.configSize`,键为 `makeKey(baseAddr, 8)`),而 `0x01` 上报只带
 * blMode / blLevel / blSpeed 三个字段,拿不到同一块里的 isColorful / rgb 字节。
 * `extract` 又必须是纯函数、不能补读硬件,因此**无法合成一个正确的配置块**。
 *
 * 强行按单字节地址回填只会写出永远命不中的僵尸条目(读侧长度是 8,回填长度是 1),
 * 并且一旦长度被"对齐"成 8,残缺的载荷会让 parser 越界。
 *
 * 所以灯光参数改为纯失效语义:驱动侧在 blMode / blLevel / blSpeed 变化时调用
 * `cache.invalidateByTag('cfg:N')`,下次读走真实硬件,保证拿到的是完整且自洽的配置块。
 * 详见 `devices/keyboard/wch-ch585m/device.ts` 的 `dispatchEvent`。
 *
 * @packageDocumentation
 */

import type { ReportBackfillRule, BackfillItem } from '../../../cache/types'
import { makeKey, addrToNumber } from '../../../cache/CacheKey'
import { ADDRESS_CH585M } from './address'
import { CMD_CH585M, PROTOCOL_CONFIG } from './cmd'
import { ch585mDeviceCustom } from '../../../types/config'
import { getAddress } from '../../../utils/common'

/** ACK 字节 - 与 sendAndWait 真实返回的 ACK 头一致 */
const READ_ACK = PROTOCOL_CONFIG.read_ack_value // 0x01
const READ_EEPROM = CMD_CH585M.read_eeprom[0]

/**
 * 构造与 sendAndWait 返回格式一致的字节快照。
 *
 * Feature report 响应布局:
 * - Byte 0: ACK (0x01 = 读取成功)
 * - Byte 1: CMD echo (0xa4 = read_eeprom)
 * - Byte 2: length
 * - Byte 3: reserved (0x00)
 * - Byte 4+: actual data bytes
 *
 * 返回的缓冲区长度固定为 `4 + length`,不足的尾部补零,确保命中缓存后
 * parser 读到 `getUint8(4 + length - 1)` 也不会越界。
 *
 * @param actualData 实际数据字节(从 offset 4 开始的部分)
 * @param length 协议层声明的读取长度,必须与读取侧的 sendRead 长度一致
 */
function buildCacheBytes(actualData: number[], length: number): Uint8Array {
  const bytes = new Uint8Array(4 + length)
  bytes[0] = READ_ACK
  bytes[1] = READ_EEPROM
  bytes[2] = length
  bytes[3] = 0x00
  bytes.set(actualData.slice(0, length), 4)
  return bytes
}

/**
 * CH585M 上报回填规则集。
 *
 * 当前仅订阅 `report:Key_user_data` 事件,只覆盖:
 * - 当前配置索引 (cfgSel) → CONFIG_CURRENT
 *
 * 灯光参数(blMode / blLevel / blSpeed)不回填,原因见文件头。
 * 电量(batPercent)也不回填(电量无对应 sendRead 调用路径)。
 */
export const CH585M_REPORT_RULES: ReportBackfillRule<ch585mDeviceCustom>[] = [
  {
    event: 'report:Key_user_data',
    extract: (userData: ch585mDeviceCustom): BackfillItem[] => {
      const firmwareCfgIdx = userData.cfgSel
      const cfgIdx = userData.cfgSlot ?? firmwareCfgIdx

      // 当前配置索引 (cfgSel)
      // getCurrentConfigIndex() 调用: sendRead(device, getAddress(CONFIG_CURRENT, 0), 5, parser)
      // parser: data.getUint8(4)
      const cfgAddr = getAddress(ADDRESS_CH585M.CONFIG_CURRENT, 0)
      const cfgReadLength = 5
      return [
        {
          key: makeKey(cfgAddr, cfgReadLength),
          data: buildCacheBytes([firmwareCfgIdx], cfgReadLength),
          tags: [`cfg:${cfgIdx}`],
          addressStart: addrToNumber(cfgAddr),
          length: cfgReadLength,
        },
      ]
    },
  },
]
